/**
 * EditStationForm Component
 * Form for editing existing stations
 */

import React, { useState } from 'react';
import StationRuntimeSummary from './StationRuntimeSummary';
import { Edit2, RefreshCw, X, Save, Clock, BarChart3, AlertTriangle, Check, Search, Zap } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DAY_NAMES } from './constants';
import ShazamLogo from './ShazamLogo';

// Helper: Convert 24h time to 12h format for display
const formatTime12h = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'μμ' : 'πμ';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Helper: Calculate duration between two times
const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return '14';
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  const diff = endMinutes - startMinutes;
  if (diff <= 0) return '0';
  return (diff / 60).toFixed(1).replace('.0', '');
};

const EditStationForm = ({
  station,
  editForm,
  setEditForm,
  editTestResult,
  editTesting,
  onTestUrl,
  onFingerprint,
  onTestMetadata,
  metadataDiagnostic,
  onClearDiagnostic,
  onSave,
  onCancel,
  apiUrl,
  token
}) => {
  const [thresholdLoading, setThresholdLoading] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [discoveryQuery, setDiscoveryQuery] = useState(editForm.name || station.name || '');
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  
  // Portal Listener Sources state
  const [portalUrl, setPortalUrl] = useState('');
  const [portalAdding, setPortalAdding] = useState(false);
  const [portalDetected, setPortalDetected] = useState(null);
  const [portalTesting, setPortalTesting] = useState(false);
  const [portalTestResult, setPortalTestResult] = useState(null);
  const [listenerSources, setListenerSources] = useState(station.listener_sources || []);
  
  // Initialize suggestions from station data
  const initialSuggestions = station.listener_thresholds_suggested && (
    station.listener_thresholds_suggested.high !== station.listener_threshold_high ||
    station.listener_thresholds_suggested.medium !== station.listener_threshold_medium ||
    station.listener_thresholds_suggested.low !== station.listener_threshold_low
  ) ? station.listener_thresholds_suggested : null;
  
  const [suggestedThresholds, setSuggestedThresholds] = useState(initialSuggestions);
  const [thresholdStats, setThresholdStats] = useState(null);
  
  const toggleDay = (dayIdx, isChecked) => {
    const newDays = isChecked
      ? [...editForm.schedule_days, dayIdx].sort()
      : editForm.schedule_days.filter(d => d !== dayIdx);
    setEditForm({ ...editForm, schedule_days: newDays });
  };
  
  // Calculate thresholds from API
  const handleCalculateThresholds = async () => {
    setThresholdLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/stream-monitor/stations/${station._id}/listener-thresholds/calculate?days=7`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.suggested_thresholds) {
        setSuggestedThresholds(data.suggested_thresholds);
        setThresholdStats(data.statistics);
      } else {
        alert(data.error || 'Δεν υπάρχουν αρκετά δεδομένα');
      }
    } catch (error) {
      console.error('Error calculating thresholds:', error);
      alert('Σφάλμα κατά τον υπολογισμό');
    }
    setThresholdLoading(false);
  };
  
  // Accept suggested thresholds
  const handleAcceptSuggestions = () => {
    if (suggestedThresholds) {
      setEditForm({
        ...editForm,
        listener_thresholds_enabled: true,
        listener_threshold_high: suggestedThresholds.high,
        listener_threshold_medium: suggestedThresholds.medium,
        listener_threshold_low: suggestedThresholds.low
      });
      setSuggestedThresholds(null);
    }
  };
  
  // Dismiss suggestions
  const handleDismissSuggestions = () => {
    setSuggestedThresholds(null);
  };

  // Auto-Discovery search
  const handleDiscoverySearch = async () => {
    if (!discoveryQuery.trim()) return;
    setDiscoveryLoading(true);
    try {
      const resp = await fetch(
        `${apiUrl}/api/stream-monitor/discover?q=${encodeURIComponent(discoveryQuery)}&country=GR`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await resp.json();
      setDiscoveryResults(data);
    } catch (e) {
      console.error('Discovery error:', e);
    }
    setDiscoveryLoading(false);
  };

  // Apply discovered IDs to the form
  const applyDiscoveredId = (field, value) => {
    setEditForm({ ...editForm, [field]: value });
  };

  // Portal Listener Sources handlers
  const handleDetectPortal = async (url) => {
    if (!url.trim()) { setPortalDetected(null); return; }
    try {
      const resp = await fetch(`${apiUrl}/api/stream-monitor/detect-portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ url: url.trim() })
      });
      const data = await resp.json();
      setPortalDetected(data);
    } catch (e) {
      setPortalDetected(null);
    }
  };

  const handleAddPortalSource = async () => {
    if (!portalUrl.trim()) return;
    setPortalAdding(true);
    try {
      const resp = await fetch(`${apiUrl}/api/stream-monitor/stations/${station._id}/listener-sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ url: portalUrl.trim() })
      });
      const data = await resp.json();
      if (data.success) {
        setListenerSources(prev => [...prev, data.source]);
        setPortalUrl('');
        setPortalDetected(null);
      } else {
        alert(data.detail || 'Error adding source');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setPortalAdding(false);
  };

  const handleRemovePortalSource = async (index) => {
    try {
      const resp = await fetch(`${apiUrl}/api/stream-monitor/stations/${station._id}/listener-sources/${index}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.success) {
        setListenerSources(prev => prev.filter((_, i) => i !== index));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleTogglePortalSource = async (index, enabled) => {
    try {
      await fetch(`${apiUrl}/api/stream-monitor/stations/${station._id}/listener-sources/${index}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ enabled })
      });
      setListenerSources(prev => prev.map((s, i) => i === index ? { ...s, enabled } : s));
    } catch (e) {
      console.error('Toggle error:', e);
    }
  };

  const handleTestPortalSources = async () => {
    setPortalTesting(true);
    setPortalTestResult(null);
    try {
      const resp = await fetch(`${apiUrl}/api/stream-monitor/stations/${station._id}/listener-sources/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      setPortalTestResult(data);
    } catch (e) {
      setPortalTestResult({ error: e.message });
    }
    setPortalTesting(false);
  };

  const PORTAL_COLORS = {
    orb: 'text-cyan-600 bg-cyan-50 border-cyan-200 dark:text-cyan-400 dark:bg-cyan-900/20 dark:border-cyan-800',
    mytuner: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/20 dark:border-emerald-800',
    eradio: 'text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-900/20 dark:border-orange-800',
    onradio: 'text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800',
    tunein: 'text-purple-600 bg-purple-50 border-purple-200 dark:text-purple-400 dark:bg-purple-900/20 dark:border-purple-800',
    radionet: 'text-rose-600 bg-rose-50 border-rose-200 dark:text-rose-400 dark:bg-rose-900/20 dark:border-rose-800',
    zenofm: 'text-teal-600 bg-teal-50 border-teal-200 dark:text-teal-400 dark:bg-teal-900/20 dark:border-teal-800',
    generic: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-900/20 dark:border-gray-700',
  };

  return (
    <Card className="bg-gray-50 dark:bg-gray-900/80 m-2 border border-gray-200 dark:border-gray-700">
      <CardContent className="pt-4 space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Edit2 className="w-4 h-4" />
          Επεξεργασία: {station.name}
        </h3>

        {/* ── Station Panel v2 — Runtime Intelligence Panels ──
            Read-only projection of the current form state against the
            actual production pipeline order. Zero backend calls, zero
            mutation of editForm.
        */}
        <StationRuntimeSummary editForm={editForm} />
        
        {/* Station ID - Copyable */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
          <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">🔑 Station ID</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white dark:bg-gray-900 px-3 py-2 rounded text-sm font-mono text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-600 select-all">
              {station._id}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(station._id);
                // Show feedback
                const btn = document.getElementById(`copy-btn-${station._id}`);
                if (btn) {
                  btn.textContent = '✓ Copied!';
                  setTimeout(() => { btn.textContent = '📋 Copy'; }, 2000);
                }
              }}
              id={`copy-btn-${station._id}`}
              className="whitespace-nowrap"
            >
              📋 Copy
            </Button>
          </div>
        </div>
        
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Όνομα σταθμού</label>
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              placeholder="Όνομα"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Περιγραφή</label>
            <Input
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              placeholder="Περιγραφή (προαιρετικό)"
              className="text-sm"
            />
          </div>
          {/* v2.10.0 — Station format hint (Music / Talk-News / Mixed).
              Saved only for now; future versions will use this to apply
              format-aware Yellow/Green thresholds on the Pipeline Health
              dashboard (e.g., a Talk station with 1 song/h is fine, a
              Music station is NOT). */}
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              Μορφή σταθμού
            </label>
            <select
              value={editForm.format || 'music'}
              onChange={(e) => setEditForm({ ...editForm, format: e.target.value })}
              data-testid="station-format-select"
              className="w-full text-sm px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            >
              <option value="music">Μουσικός σταθμός</option>
              <option value="talk_news">Talk / News</option>
              <option value="mixed">Μικτός (μουσική + ομιλία)</option>
            </select>
            <p className="text-[10px] text-gray-500 dark:text-gray-500 mt-1">
              Επιλέξτε &quot;Talk / News&quot; για σταθμούς που εκπέμπουν κυρίως ομιλία ή ειδήσεις
              — επηρεάζει μελλοντικές κατηγοριοποιήσεις της κατάστασης pipeline.
            </p>
          </div>
        </div>
        
        {/* URLs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🎵 Stream URL <span className="text-blue-500">(για ICY metadata)</span>
            </label>
            <Input
              value={editForm.stream_url}
              onChange={(e) => setEditForm({ ...editForm, stream_url: e.target.value })}
              placeholder="https://stream..."
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🌐 Website URL <span className="text-cyan-500">(auto-detect scraping)</span>
            </label>
            <Input
              data-testid="edit-station-website-url"
              value={editForm.website_url || ''}
              onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
              placeholder="π.χ. https://radoxo.com/greece/... ή https://station.gr/"
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🔗 Metadata API <span className="text-orange-500">(artist/title - προαιρετικό)</span>
            </label>
            <Input
              value={editForm.metadata_api_url || ''}
              onChange={(e) => setEditForm({ ...editForm, metadata_api_url: e.target.value })}
              placeholder="https://api... (custom metadata endpoint)"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🌐 OnlineRadioBox ID <span className="text-cyan-500">(π.χ. gr/loud)</span>
            </label>
            <Input
              data-testid="onlineradiobox-id-input"
              value={editForm.onlineradiobox_id || ''}
              onChange={(e) => setEditForm({ ...editForm, onlineradiobox_id: e.target.value })}
              placeholder="gr/loud, gr/stathmos995..."
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              🎵 myTuner ID <span className="text-emerald-500">(αριθμητικό ID από mytuner-radio.com)</span>
            </label>
            <Input
              data-testid="mytuner-id-input"
              value={editForm.mytuner_id || ''}
              onChange={(e) => setEditForm({ ...editForm, mytuner_id: e.target.value })}
              placeholder="413956, 439229..."
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              Metadata Source Priority <span className="text-gray-400">(legacy)</span>
            </label>
            <select
              data-testid="metadata-source-priority"
              value={editForm.metadata_source_priority || 'auto'}
              onChange={(e) => setEditForm({ ...editForm, metadata_source_priority: e.target.value })}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <option value="auto">Auto</option>
              <option value="mytuner_first">myTuner πρώτα</option>
              <option value="orb_first">OnlineRadioBox πρώτα</option>
              <option value="external_only">Μόνο εξωτερικές πηγές (χωρίς ICY)</option>
            </select>
          </div>
        </div>
        
        {/* Metadata Source Toggles */}
        <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3 bg-blue-50/50 dark:bg-blue-900/20">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">
            Ενεργές Πηγές Metadata
          </h4>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
            Επιλέξτε ποιες πηγές θα χρησιμοποιούνται για αναγνώριση τραγουδιών
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="flex items-center gap-2 cursor-pointer p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                data-testid="source-icy-checkbox"
                checked={editForm.source_icy_enabled !== false}
                onChange={(e) => setEditForm({ ...editForm, source_icy_enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ICY Stream</span>
                <p className="text-[10px] text-gray-400">Metadata από stream</p>
              </div>
            </label>
            {/* OnlineRadioBox source: auto-disabled & non-interactive when no ID is set.
                Prevents user confusion that an unconfigured source might be "overriding" ICY. */}
            <label
              className={`flex items-center gap-2 p-2 rounded-md border transition-colors ${
                editForm.onlineradiobox_id
                  ? 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                  : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
              }`}
              title={!editForm.onlineradiobox_id ? 'Συμπληρώστε OnlineRadioBox ID για να ενεργοποιηθεί' : ''}
            >
              <input
                type="checkbox"
                data-testid="source-orb-checkbox"
                disabled={!editForm.onlineradiobox_id}
                checked={!!editForm.onlineradiobox_id && editForm.source_orb_enabled !== false}
                onChange={(e) => setEditForm({ ...editForm, source_orb_enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 disabled:cursor-not-allowed"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">OnlineRadioBox</span>
                <p className={`text-[10px] ${editForm.onlineradiobox_id ? 'text-gray-400' : 'text-amber-500 dark:text-amber-400 font-medium'}`}>
                  {editForm.onlineradiobox_id ? `ID: ${editForm.onlineradiobox_id}` : 'Ανενεργό — δεν έχει οριστεί ID'}
                </p>
              </div>
            </label>
            {/* myTuner source: auto-disabled & non-interactive when no ID is set. */}
            <label
              className={`flex items-center gap-2 p-2 rounded-md border transition-colors ${
                editForm.mytuner_id
                  ? 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer'
                  : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
              }`}
              title={!editForm.mytuner_id ? 'Συμπληρώστε myTuner ID για να ενεργοποιηθεί' : ''}
            >
              <input
                type="checkbox"
                data-testid="source-mytuner-checkbox"
                disabled={!editForm.mytuner_id}
                checked={!!editForm.mytuner_id && editForm.source_mytuner_enabled !== false}
                onChange={(e) => setEditForm({ ...editForm, source_mytuner_enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:cursor-not-allowed"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">myTuner</span>
                <p className={`text-[10px] ${editForm.mytuner_id ? 'text-gray-400' : 'text-amber-500 dark:text-amber-400 font-medium'}`}>
                  {editForm.mytuner_id ? `ID: ${editForm.mytuner_id}` : 'Ανενεργό — δεν έχει οριστεί ID'}
                </p>
              </div>
            </label>
          </div>
        </div>
        
        <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-3 bg-orange-50/50 dark:bg-orange-900/20" data-testid="first-play-alert-section">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="first-play-alert-checkbox"
              checked={editForm.first_play_alert_enabled === true}
              onChange={(e) => setEditForm({ ...editForm, first_play_alert_enabled: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Ημερήσιο email «Πρώτες μεταδόσεις»</span>
          </label>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Κάθε πρωί στις 07:00 στέλνονται τα τραγούδια που ο σταθμός μετέδωσε χθες και δεν είχε μεταδώσει στο διάστημα που ορίζεται στις Ρυθμίσεις Συστήματος → Πρώτες Μεταδ.
          </p>
          {editForm.first_play_alert_enabled && (
            <Input
              data-testid="first-play-alert-emails-input"
              value={editForm.first_play_alert_emails || ''}
              onChange={(e) => setEditForm({ ...editForm, first_play_alert_emails: e.target.value })}
              placeholder="email1@example.com, email2@example.com"
              className="text-sm mt-2"
            />
          )}
        </div>

        {/* Reverse Artist/Title Fix */}
        <div className="mt-3">
          <label className="flex items-center gap-2 cursor-pointer p-2 rounded-md border border-amber-300/50 dark:border-amber-600/30 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
            <input
              type="checkbox"
              data-testid="reverse-artist-title-checkbox"
              checked={editForm.reverse_artist_title === true}
              onChange={(e) => setEditForm({ ...editForm, reverse_artist_title: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <span className="text-sm font-medium text-amber-600 dark:text-amber-400">Fix reversed Artist/Title</span>
              <p className="text-[10px] text-gray-400">Αντιστρέφει Artist ↔ Title όταν είναι ανάποδα (per-station)</p>
            </div>
          </label>
        </div>
        
        {/* Auto-Discovery Button */}
        <div>
          <Button
            data-testid="auto-discover-btn"
            variant="outline"
            size="sm"
            onClick={() => { setDiscoveryOpen(!discoveryOpen); if (!discoveryResults) handleDiscoverySearch(); }}
            className="text-xs gap-1.5 border-dashed border-violet-400 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20"
          >
            <Zap className="w-3.5 h-3.5" />
            Auto-Discovery IDs
          </Button>
        </div>

        {/* Discovery Results Panel */}
        {discoveryOpen && (
          <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Input
                data-testid="discovery-search-input"
                value={discoveryQuery}
                onChange={(e) => setDiscoveryQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDiscoverySearch()}
                placeholder="Αναζήτηση σταθμού..."
                className="text-sm flex-1"
              />
              <Button size="sm" onClick={handleDiscoverySearch} disabled={discoveryLoading} className="text-xs">
                {discoveryLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDiscoveryOpen(false)} className="text-xs px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>

            {discoveryResults && (
              <div className="space-y-2 text-xs max-h-64 overflow-y-auto">
                {discoveryResults.mytuner?.length > 0 && (
                  <div>
                    <div className="font-semibold text-emerald-600 dark:text-emerald-400 mb-1">myTuner Radio ({discoveryResults.mytuner.length})</div>
                    {discoveryResults.mytuner.map((r, i) => (
                      <div key={`mt-${i}`} className="flex items-center justify-between py-1 px-2 rounded bg-white/60 dark:bg-gray-800/40 mb-1">
                        <span className="truncate flex-1">{r.name} <span className="text-gray-400">#{r.mytuner_id}</span></span>
                        <Button
                          data-testid={`apply-mytuner-${r.mytuner_id}`}
                          size="sm" variant="ghost"
                          onClick={() => applyDiscoveredId('mytuner_id', r.mytuner_id)}
                          className="text-xs h-6 px-2 text-emerald-600 hover:text-emerald-800"
                        >
                          <Check className="w-3 h-3 mr-1" /> Εφαρμογή
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {discoveryResults.onlineradiobox?.length > 0 && (
                  <div>
                    <div className="font-semibold text-cyan-600 dark:text-cyan-400 mb-1">OnlineRadioBox ({discoveryResults.onlineradiobox.length})</div>
                    {discoveryResults.onlineradiobox.map((r, i) => (
                      <div key={`orb-${i}`} className="flex items-center justify-between py-1 px-2 rounded bg-white/60 dark:bg-gray-800/40 mb-1">
                        <span className="truncate flex-1">
                          {r.name} <span className="text-gray-400">{r.onlineradiobox_id}</span>
                          {r.has_playlist_data && <span className="ml-1 text-green-500" title="Has playlist data">✓</span>}
                          {!r.has_playlist_data && <span className="ml-1 text-orange-400" title="No playlist data">○</span>}
                        </span>
                        <Button
                          data-testid={`apply-orb-${r.onlineradiobox_id}`}
                          size="sm" variant="ghost"
                          onClick={() => applyDiscoveredId('onlineradiobox_id', r.onlineradiobox_id)}
                          className="text-xs h-6 px-2 text-cyan-600 hover:text-cyan-800"
                        >
                          <Check className="w-3 h-3 mr-1" /> Εφαρμογή
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {discoveryResults.radio_browser?.length > 0 && (
                  <div>
                    <div className="font-semibold text-blue-600 dark:text-blue-400 mb-1">Radio Browser ({discoveryResults.radio_browser.length})</div>
                    {discoveryResults.radio_browser.map((r, i) => (
                      <div key={`rb-${i}`} className="flex items-center justify-between py-1 px-2 rounded bg-white/60 dark:bg-gray-800/40 mb-1">
                        <span className="truncate flex-1">{r.name} <span className="text-gray-400">{r.codec} {r.bitrate}kbps</span></span>
                        {r.stream_url && (
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => applyDiscoveredId('stream_url', r.stream_url)}
                            className="text-xs h-6 px-2 text-blue-600 hover:text-blue-800"
                          >
                            <Check className="w-3 h-3 mr-1" /> Stream URL
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {discoveryResults.total_results === 0 && (
                  <div className="text-center text-gray-500 py-2">Δεν βρέθηκαν αποτελέσματα</div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Listeners URLs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              📊 Stats URL <span className="text-green-500">(listeners - Icecast/Shoutcast)</span>
            </label>
            <Input
              value={editForm.stats_url || ''}
              onChange={(e) => setEditForm({ ...editForm, stats_url: e.target.value })}
              placeholder="https://.../status-json.xsl ή /7.html"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              👥 Listeners API <span className="text-purple-500">(αν διαφέρει - προαιρετικό)</span>
            </label>
            <Input
              value={editForm.listeners_api_url || ''}
              onChange={(e) => setEditForm({ ...editForm, listeners_api_url: e.target.value })}
              placeholder="https://... (ξεχωριστό API για ακροατές)"
              className="text-sm"
            />
          </div>
        </div>

        {/* v2.9.159 — Aggregation mode for multi-source listener counts.
            "max" (default) keeps the historical behavior (largest of
            stats_url vs listeners_api_url wins). "sum" adds them — use
            this only when both URLs measure DIFFERENT delivery endpoints
            (e.g., HD + low-bitrate). Same-endpoint sources will double-
            count under sum mode. */}
        {(editForm.stats_url || editForm.listeners_api_url) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3" data-testid="aggregation-mode-row">
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                🔢 Συνδυασμός πηγών ακροατών
              </label>
              <select
                value={editForm.listeners_aggregation_mode || 'max'}
                onChange={(e) => setEditForm({ ...editForm, listeners_aggregation_mode: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                data-testid="listeners-aggregation-mode-select"
              >
                <option value="max">Max — η μεγαλύτερη τιμή νικάει (default)</option>
                <option value="sum">Sum — άθροιση όλων των πηγών</option>
              </select>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                {editForm.listeners_aggregation_mode === 'sum'
                  ? '⚠️ Use only when sources measure DIFFERENT delivery endpoints (HD + mobile + radio). Same-endpoint sources will double-count.'
                  : 'Παίρνει τον μεγαλύτερο αριθμό από stats_url & listeners_api_url.'}
              </p>
            </div>
          </div>
        )}

        {/* v2.9.46: Per-station Poll Interval Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label
              htmlFor="poll-interval-select"
              className="text-xs text-gray-600 dark:text-gray-400 mb-1 block"
              data-testid="poll-interval-label"
            >
              ⏱ Poll Interval <span className="text-yellow-600 dark:text-yellow-400">(συχνότητα δειγματοληψίας ακροατών)</span>
            </label>
            <select
              id="poll-interval-select"
              data-testid="poll-interval-select"
              value={
                [10, 15, 20, 30].includes(Number(editForm.poll_interval_seconds))
                  ? Number(editForm.poll_interval_seconds)
                  : 30
              }
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  poll_interval_seconds: parseInt(e.target.value, 10),
                })
              }
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            >
              <option value={10}>10s — high-priority station (3× polls)</option>
              <option value={15}>15s — medium-priority (2× polls)</option>
              <option value={20}>20s — light boost</option>
              <option value={30}>30s — default</option>
            </select>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 pb-1">
            Default 30s. Χαμηλότερο = περισσότερα samples ανά τραγούδι (καλύτερα retention/LSI/early-drop), αλλά αναλογικά περισσότερα requests προς το stats URL του σταθμού.
          </div>
        </div>
        
        {/* Advanced Metrics URL Section */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
              📈 Advanced Metrics API URL <span className="text-cyan-500">(JSON - proxy retention, LSI, TLH κλπ)</span>
            </label>
            <Input
              value={editForm.advanced_metrics_url || ''}
              onChange={(e) => setEditForm({ ...editForm, advanced_metrics_url: e.target.value })}
              placeholder="https://radio-apps.online/api/stations/{station_id}/stats"
              className="text-sm"
              data-testid="advanced-metrics-url-input"
            />
            <p className="text-xs text-gray-400 mt-1">
              Προαιρετικό - JSON endpoint για proxy audience metrics (retention, LSI, TLH, volatility)
            </p>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════
            PORTAL LISTENER SOURCES — Multi-portal aggregation
            ═══════════════════════════════════════════════════ */}
        <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-3 bg-amber-50/50 dark:bg-amber-900/20">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Πηγές Ακροατών (Portals)
            </h4>
            <span className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded-full">
              {listenerSources.length} {listenerSources.length === 1 ? 'πηγή' : 'πηγές'}
            </span>
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-3">
            Προσθέστε URLs από radio portals (ORB, myTuner, e-radio κλπ). Οι ακροατές αθροίζονται.
          </p>

          {/* Existing Sources List */}
          {listenerSources.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {listenerSources.map((source, idx) => (
                <div
                  key={idx}
                  data-testid={`portal-source-${idx}`}
                  className={`flex items-center gap-2 p-2 rounded-md border text-xs ${
                    PORTAL_COLORS[source.portal_type] || PORTAL_COLORS.generic
                  } ${!source.enabled ? 'opacity-50' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={source.enabled !== false}
                    onChange={(e) => handleTogglePortalSource(idx, e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-gray-300"
                    data-testid={`portal-toggle-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">{source.display_name || source.portal_type}</span>
                    <span className="text-gray-400 ml-1 truncate block text-[10px]" title={source.url}>{source.url}</span>
                    {source.last_listeners != null && (
                      <span className="text-[10px] opacity-70">
                        Τελευταίο: {source.last_listeners} ακροατές
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemovePortalSource(idx)}
                    className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-500 transition-colors"
                    data-testid={`portal-remove-${idx}`}
                    title="Αφαίρεση"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add New Source */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                data-testid="portal-url-input"
                value={portalUrl}
                onChange={(e) => {
                  setPortalUrl(e.target.value);
                  // Auto-detect on paste or after typing
                  if (e.target.value.length > 10) handleDetectPortal(e.target.value);
                }}
                onPaste={(e) => {
                  setTimeout(() => handleDetectPortal(e.target.value), 100);
                }}
                placeholder="Paste URL: https://onlineradiobox.com/gr/..."
                className="text-sm"
              />
              {portalDetected && (
                <p className="text-[10px] mt-1 text-amber-600 dark:text-amber-400">
                  Detected: <strong>{portalDetected.display_name}</strong>
                  {portalDetected.portal_id && ` (ID: ${portalDetected.portal_id})`}
                </p>
              )}
            </div>
            <Button
              data-testid="portal-add-btn"
              size="sm"
              onClick={handleAddPortalSource}
              disabled={!portalUrl.trim() || portalAdding}
              className="text-xs gap-1 whitespace-nowrap bg-amber-600 hover:bg-amber-700 text-white"
            >
              {portalAdding ? <RefreshCw className="w-3 h-3 animate-spin" /> : '+'}
              Προσθήκη
            </Button>
          </div>

          {/* Test All Sources Button */}
          {listenerSources.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                data-testid="portal-test-btn"
                variant="outline"
                size="sm"
                onClick={handleTestPortalSources}
                disabled={portalTesting}
                className="text-xs gap-1 border-amber-400 text-amber-600 dark:text-amber-400"
              >
                {portalTesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Test Πηγές
              </Button>
              {portalTestResult && (
                <span className="text-xs text-amber-700 dark:text-amber-300">
                  {portalTestResult.error
                    ? `Error: ${portalTestResult.error}`
                    : `Σύνολο: ${portalTestResult.total_listeners} ακροατές (${portalTestResult.enabled_count} ενεργές πηγές)`
                  }
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Schedule Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Χρονοπρογραμματισμός
            </h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.schedule_enabled}
                onChange={(e) => setEditForm({ ...editForm, schedule_enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">Ενεργό</span>
            </label>
          </div>
          
          {editForm.schedule_enabled && (
            <>
              {/* Days of Week */}
              <div className="mb-3">
                <label className="text-xs text-gray-600 dark:text-gray-400 mb-2 block">Ημέρες λειτουργίας</label>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((day, idx) => (
                    <label key={idx} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.schedule_days.includes(idx)}
                        onChange={(e) => toggleDay(idx, e.target.checked)}
                        className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`text-xs px-2 py-1 rounded ${
                        editForm.schedule_days.includes(idx) 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {day}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Time Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Ώρα έναρξης (24ωρη, Ελλάδα)</label>
                  <Input
                    type="time"
                    value={editForm.schedule_start_time}
                    onChange={(e) => setEditForm({ ...editForm, schedule_start_time: e.target.value })}
                    className="text-sm"
                  />
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-1">
                    24ωρη μορφή: {editForm.schedule_start_time || '06:00'} = {formatTime12h(editForm.schedule_start_time || '06:00')}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Ώρα λήξης (24ωρη, Ελλάδα)</label>
                  <Input
                    type="time"
                    value={editForm.schedule_end_time}
                    onChange={(e) => setEditForm({ ...editForm, schedule_end_time: e.target.value })}
                    className="text-sm"
                  />
                  <p className="text-[10px] text-blue-500 dark:text-blue-400 mt-1">
                    24ωρη μορφή: {editForm.schedule_end_time || '20:00'} = {formatTime12h(editForm.schedule_end_time || '20:00')}
                  </p>
                </div>
              </div>
              
              {/* Schedule Summary */}
              <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs">
                <span className="text-blue-700 dark:text-blue-300">
                  Το πρόγραμμα θα τρέχει από <strong>{editForm.schedule_start_time || '06:00'}</strong> έως <strong>{editForm.schedule_end_time || '20:00'}</strong> (Ώρα Ελλάδας)
                  {' '}({calculateDuration(editForm.schedule_start_time, editForm.schedule_end_time)} ώρες)
                </span>
                {editForm.schedule_end_time && editForm.schedule_start_time && 
                 editForm.schedule_end_time <= editForm.schedule_start_time && (
                  <p className="text-red-500 mt-1 font-semibold">
                    ⚠️ Η ώρα λήξης πρέπει να είναι μετά την ώρα έναρξης!
                  </p>
                )}
              </div>
              
              {/* Manual Override Info */}
              {editForm.manual_override && (
                <div className="mt-3 p-2 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-700 dark:text-yellow-400">
                  ⚡ Χειροκίνητο override ενεργό - Ο σταθμός αγνοεί το πρόγραμμα.
                  <button
                    onClick={() => setEditForm({ ...editForm, manual_override: false })}
                    className="ml-2 underline hover:text-yellow-600 dark:hover:text-yellow-300"
                  >
                    Επαναφορά
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Listener Thresholds Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Κατώφλια Ακροατών
            </h4>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleCalculateThresholds} 
                disabled={thresholdLoading} 
                size="sm" 
                variant="outline"
                className="text-xs"
              >
                {thresholdLoading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                Υπολογισμός
              </Button>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.listener_thresholds_enabled || false}
                  onChange={(e) => setEditForm({ ...editForm, listener_thresholds_enabled: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-600 dark:text-gray-400">Ενεργό</span>
              </label>
            </div>
          </div>
          
          {/* New Suggestions Alert */}
          {suggestedThresholds && (
            <div className="mb-3 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                    Νέες προτεινόμενες τιμές: {suggestedThresholds.high}, {suggestedThresholds.medium}, {suggestedThresholds.low}
                  </p>
                  {thresholdStats && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                      Βάσει τελευταίων 7 ημερών (min: {thresholdStats.min}, max: {thresholdStats.max}, avg: {thresholdStats.avg})
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={handleAcceptSuggestions} className="text-xs bg-green-600 hover:bg-green-700">
                      <Check className="w-3 h-3 mr-1" /> Αποδοχή
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleDismissSuggestions} className="text-xs">
                      Αγνόηση
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Threshold Input Fields */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                Υψηλοί (75%) <span className="text-green-500">●</span>
              </label>
              <Input
                type="number"
                value={editForm.listener_threshold_high || ''}
                onChange={(e) => setEditForm({ ...editForm, listener_threshold_high: parseInt(e.target.value) || null })}
                placeholder="π.χ. 18"
                className="text-sm"
                disabled={!editForm.listener_thresholds_enabled}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                Μέσοι (50%) <span className="text-yellow-500">●</span>
              </label>
              <Input
                type="number"
                value={editForm.listener_threshold_medium || ''}
                onChange={(e) => setEditForm({ ...editForm, listener_threshold_medium: parseInt(e.target.value) || null })}
                placeholder="π.χ. 12"
                className="text-sm"
                disabled={!editForm.listener_thresholds_enabled}
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">
                Χαμηλοί (25%) <span className="text-red-500">●</span>
              </label>
              <Input
                type="number"
                value={editForm.listener_threshold_low || ''}
                onChange={(e) => setEditForm({ ...editForm, listener_threshold_low: parseInt(e.target.value) || null })}
                placeholder="π.χ. 8"
                className="text-sm"
                disabled={!editForm.listener_thresholds_enabled}
              />
            </div>
          </div>
          
          {editForm.listener_thresholds_enabled && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
              ℹ️ Τιμές: ≥Υψηλοί = αξιόπιστα • ≥Μέσοι = προσέγγιση • ≥Χαμηλοί = χαμηλή εμπιστοσύνη • &lt;Χαμηλοί = αναξιόπιστα
            </p>
          )}
        </div>
        
        {/* Radio Chart Inclusion */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editForm.include_in_chart || false}
              onChange={(e) => setEditForm({ ...editForm, include_in_chart: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              📊 Συμμετοχή στο Radio Chart
            </span>
            <span className="text-xs text-gray-500">(Top 300)</span>
          </label>
        </div>
        
        {/* Chart Influence Level */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Chart Influence
            </h4>
            <div className="group relative">
              <span className="text-gray-400 cursor-help text-xs">(?)</span>
              <div className="hidden group-hover:block absolute z-50 right-0 bottom-full mb-2 w-64 p-2 text-xs text-white bg-gray-900 border border-gray-700 rounded-lg shadow-xl">
                Defines how strongly this station influences radio charts. Lower values indicate higher chart impact.
              </div>
            </div>
          </div>
          <select
            value={editForm.chart_influence_level ?? 5}
            onChange={(e) => setEditForm({ ...editForm, chart_influence_level: parseInt(e.target.value) })}
            className="w-full text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="chart-influence-select"
          >
            <option value={1}>1 - Highest Influence</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            <option value={5}>5 - Medium Influence</option>
            <option value={6}>6</option>
            <option value={7}>7</option>
            <option value={8}>8</option>
            <option value={9}>9</option>
            <option value={10}>10 - Lowest Influence</option>
          </select>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
            1 = Πολύ υψηλή επιρροή (factor: 1.00) | 5 = Μέση επιρροή (factor: 0.80) | 10 = Ελάχιστη (factor: 0.30)
          </p>
        </div>
        
        {/* Fingerprint Settings */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editForm.fingerprint_enabled !== false}
              onChange={(e) => setEditForm({ ...editForm, fingerprint_enabled: e.target.checked, ...(e.target.checked ? {} : { fingerprint_unlimited: false }) })}
              className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              data-testid="fingerprint-enabled-checkbox"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Audio Fingerprint
            </span>
            <span className="text-xs text-gray-500">(αναγνώριση από ήχο - fallback)</span>
          </label>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Χρησιμοποιείται ως εναλλακτική πηγή αν οι παραπάνω πηγές δεν βρουν τραγούδι
          </p>
          
          {/* Unlimited Fingerprint - only shown when fingerprint is enabled */}
          {editForm.fingerprint_enabled !== false && (
            <div className="ml-6 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.fingerprint_unlimited === true}
                  onChange={(e) => setEditForm({ ...editForm, fingerprint_unlimited: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  data-testid="fingerprint-unlimited-checkbox"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  FINGERPRINT FIRST (Απεριόριστο)
                </span>
              </label>
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 ml-6">
                Το fingerprint τρέχει ΠΡΩΤΟ πριν τις άλλες πηγές. Αν δεν βρει αποτέλεσμα, πέφτει στις ενεργοποιημένες πηγές ως backup.
                Χρησιμοποιεί{' '}
                <span className="inline-flex items-center gap-1 align-middle">
                  <ShazamLogo size={11} />
                  <strong>Shazam</strong>
                </span>{' '}
                — <strong>δωρεάν, απεριόριστες κλήσεις</strong>, χωρίς όρια χρήσης.
              </p>

              {/* v2.9.80 — Visual hint: stations without ICY need this ON */}
              {editForm.fingerprint_unlimited !== true && (
                <div
                  className="mt-2 ml-6 flex items-start gap-2 px-2 py-1.5 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800"
                  data-testid="fingerprint-first-hint-shazam"
                >
                  <ShazamLogo size={16} className="mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] leading-relaxed text-blue-700 dark:text-blue-300">
                    Αν ο σταθμός <strong>δεν στέλνει ICY metadata</strong> (κενός τίτλος ή μόνο το όνομα του σταθμού),
                    ενεργοποίησε το FINGERPRINT FIRST. Η αναγνώριση γίνεται με Shazam <strong>δωρεάν</strong> και τρέχει σε real-time.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* v2.13.140 — Shazam Fallback (ICY Stuck Rescue) manual ON/OFF */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editForm.icy_stuck_rescue_enabled !== false}
              onChange={(e) => setEditForm({ ...editForm, icy_stuck_rescue_enabled: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              data-testid="icy-stuck-rescue-checkbox"
            />
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              <ShazamLogo size={14} />
              Shazam Fallback
            </span>
            <span className="text-xs text-gray-500">(διάσωση όταν κολλήσει το ICY)</span>
          </label>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 ml-6">
            Όταν το ICY metadata μείνει <strong>κολλημένο στον ίδιο τίτλο για 5+ λεπτά</strong> (π.χ. live εκπομπή,
            μαγνητοσκοπημένο πρόγραμμα), το σύστημα ακούει τον σταθμό με Shazam ώστε να μη χάνονται plays.
            Το ICY έχει <strong>πάντα προτεραιότητα</strong> — το fallback ενεργοποιείται ΜΟΝΟ αφού το ICY κολλήσει.
          </p>
          {editForm.icy_stuck_rescue_enabled === false && (
            <p
              className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 ml-6"
              data-testid="icy-stuck-rescue-off-warning"
            >
              ⚠️ Απενεργοποιημένο: αν το ICY κολλήσει, τα plays του σταθμού θα χάνονται μέχρι να ξεκολλήσει.
            </p>
          )}
        </div>

        {/* v2.9.78 — Recognition Provider (per-station Shazam/ICY)
            v2.13.151 (2026-02-01) — AudD αφαιρέθηκε από τη default αλυσίδα
            και από το dropdown. Δίνεται πλέον μέσω του ξεχωριστού
            "Μόνο AudD" checkbox παρακάτω (opt-in, per station). */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              🎯 Πάροχος Αναγνώρισης Τραγουδιού
            </span>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 mb-2">
              Πώς ταυτοποιείται το τραγούδι όταν λείπει ή είναι ασαφές το ICY metadata.
              Default: <strong>Auto</strong> (ICY → cache → Shazam). Χρησιμοποιείται Shazam <strong>δωρεάν</strong> για όλες τις αναγνωρίσεις.
            </p>
            <select
              value={
                editForm.fingerprint_provider === 'audd'
                  ? 'auto'
                  : (editForm.fingerprint_provider || 'auto')
              }
              onChange={(e) =>
                setEditForm({ ...editForm, fingerprint_provider: e.target.value })
              }
              className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              data-testid="fingerprint-provider-select"
              disabled={editForm.fingerprint_provider === 'audd'}
            >
              <option value="auto">
                Auto — ICY → Cache → Shazam (συνιστώμενο)
              </option>
              <option value="shazam">
                Shazam-only — μόνο shazamio (αγνοεί ICY/API/website)
              </option>
              <option value="icy">
                ICY-only — μόνο stream metadata, χωρίς fingerprinting
              </option>
              <option value="disabled">
                Disabled — απενεργοποίηση recognition
              </option>
            </select>

            {/* v2.9.81 — Warning when Shazam-only is selected */}
            {editForm.fingerprint_provider === 'shazam' && (
              <div
                className="mt-2 flex items-start gap-2 px-2 py-1.5 rounded-md bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800"
                data-testid="shazam-only-mode-warning"
              >
                <ShazamLogo size={16} className="mt-0.5 flex-shrink-0" />
                <p className="text-[10px] leading-relaxed text-orange-700 dark:text-orange-300">
                  <strong>Προσοχή:</strong> Σε Shazam-only mode αγνοείται εντελώς το ICY metadata, η station API,
                  το OnlineRadioBox και το website scraping. <u>Μόνο</u> οι αναγνωρίσεις του Shazam δημιουργούν εγγραφές
                  τραγουδιών. Ιδανικό για σταθμούς που στέλνουν ψεύτικα/παραπλανητικά metadata
                  (π.χ. fake international ονόματα ενώ παίζουν ελληνικά).
                  Το FINGERPRINT FIRST + Audio Fingerprint ενεργοποιούνται αυτόματα.
                </p>
              </div>
            )}
          </label>

          {/* v2.13.172 (2026-02) — AudD gated to explicit per-station opt-in.
              Global default: AUDD_ENABLED=false (paid, opt-in). The
              checkbox below is the ONLY path that can trigger AudD.
              `provider=auto` and every other combination NEVER calls
              AudD after v2.13.172. */}
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editForm.fingerprint_provider === 'audd'}
                onChange={(e) => {
                  if (e.target.checked) {
                    setEditForm({ ...editForm, fingerprint_provider: 'audd' });
                  } else {
                    setEditForm({ ...editForm, fingerprint_provider: 'auto' });
                  }
                }}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                data-testid="audd-only-checkbox"
              />
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  🔴 Μόνο AudD
                </span>
                <span className="ml-2 text-xs text-red-600 dark:text-red-400 font-semibold">
                  (επί πληρωμή)
                </span>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  Παρακάμπτει τον πάροχο παραπάνω και χρησιμοποιεί μόνο το AudD για αυτόν τον σταθμό.
                  <strong className="text-red-600 dark:text-red-400"> Κάθε αναγνώριση χρεώνεται.</strong>
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  <strong>Κανένα άλλο mode δεν καλεί AudD.</strong> Το «Auto», «Shazam-only», «ICY-only», το «Fingerprint FIRST», το «Shazam Fallback (ICY Stuck Rescue)» και το «Απεριόριστο fingerprint» χρησιμοποιούν αποκλειστικά <strong>Shazam</strong> (δωρεάν).
                </p>
                {editForm.fingerprint_provider === 'audd' && (
                  <p
                    className="text-[10px] text-red-700 dark:text-red-300 mt-1"
                    data-testid="audd-only-mode-warning"
                  >
                    ⚠️ Ο σταθμός είναι σε AudD-only mode. Απαιτεί <code>AUDD_ENABLED=true</code> + valid API key στο backend. Η κάθε αναγνώριση χρεώνεται.
                  </p>
                )}
              </div>
            </label>
          </div>
        </div>
        
        {/* Data Mode Selection */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            📊 Πηγή Δεδομένων (Data Mode)
          </h4>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="data_mode"
                value="live"
                checked={editForm.data_mode === 'live'}
                onChange={(e) => setEditForm({ ...editForm, data_mode: e.target.value })}
                className="w-4 h-4 text-green-600 focus:ring-green-500"
              />
              <span className={`text-sm px-2 py-1 rounded ${
                editForm.data_mode === 'live' 
                  ? 'bg-green-500/20 text-green-700 dark:text-green-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                Live Audience
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="data_mode"
                value="stream"
                checked={editForm.data_mode === 'stream'}
                onChange={(e) => setEditForm({ ...editForm, data_mode: e.target.value })}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className={`text-sm px-2 py-1 rounded ${
                editForm.data_mode === 'stream' 
                  ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400 font-medium' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                Stream Analytics
              </span>
            </label>
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
            <strong>Live Audience:</strong> Πραγματικοί ακροατές από stats URL • 
            <strong> Stream Analytics:</strong> Signal-based ανάλυση χωρίς πραγματικούς ακροατές
          </p>
        </div>
        
        {/* Station Classification */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            🏷️ Κατηγοριοποίηση Σταθμού
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Τύπος Μουσικής</label>
              <select
                data-testid="music-type-select"
                value={editForm.music_type || ''}
                onChange={(e) => setEditForm({ ...editForm, music_type: e.target.value || null })}
                className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">-- Χωρίς --</option>
                <option value="greek">Ελληνική</option>
                <option value="international">Διεθνής / Ξένη</option>
                <option value="rock">Ροκ</option>
                <option value="entechno">Έντεχνη</option>
                <option value="mixed">Μικτή</option>
                <option value="classic_hits">Classic Hits</option>
                <option value="sophisticated">Sophisticated</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Όμιλος</label>
              <select
                data-testid="station-group-select"
                value={editForm.station_group || ''}
                onChange={(e) => setEditForm({ ...editForm, station_group: e.target.value || null })}
                className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">-- Χωρίς --</option>
                <option value="group_antenna">Group Antenna</option>
                <option value="group_attikes">Group Αττικές Εκδόσεις</option>
                <option value="group_audiomax">Group Audiomax</option>
                <option value="group_frontstage">Group Frontstage</option>
                <option value="group_kostaki">Group Κωστάκη</option>
                <option value="group_politis">Group Politis</option>
                <option value="group_skai">Group Skai</option>
                <option value="group_independent">Group Ανεξάρτητος</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Νομός / Περιφέρεια</label>
            <select
              data-testid="station-prefecture-select"
              value={editForm.prefecture || ''}
              onChange={(e) => setEditForm({ ...editForm, prefecture: e.target.value || null })}
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">-- Χωρίς --</option>
              <optgroup label="Αττική">
                <option value="attiki">Αττική</option>
              </optgroup>
              <optgroup label="Κεντρική Μακεδονία">
                <option value="thessaloniki">Θεσσαλονίκη</option>
                <option value="chalkidiki">Χαλκιδική</option>
                <option value="imathia">Ημαθία</option>
                <option value="kilkis">Κιλκίς</option>
                <option value="pella">Πέλλα</option>
                <option value="pieria">Πιερία</option>
                <option value="serres">Σέρρες</option>
              </optgroup>
              <optgroup label="Δυτική Μακεδονία">
                <option value="kastoria">Καστοριά</option>
                <option value="kozani">Κοζάνη</option>
                <option value="florina">Φλώρινα</option>
                <option value="grevena">Γρεβενά</option>
              </optgroup>
              <optgroup label="Ανατολική Μακεδονία & Θράκη">
                <option value="drama">Δράμα</option>
                <option value="evros">Έβρος</option>
                <option value="kavala">Καβάλα</option>
                <option value="xanthi">Ξάνθη</option>
                <option value="rodopi">Ροδόπη</option>
                <option value="thasos">Θάσος</option>
              </optgroup>
              <optgroup label="Ήπειρος">
                <option value="ioannina">Ιωάννινα</option>
                <option value="arta">Άρτα</option>
                <option value="preveza">Πρέβεζα</option>
                <option value="thesprotia">Θεσπρωτία</option>
              </optgroup>
              <optgroup label="Θεσσαλία">
                <option value="larisa">Λάρισα</option>
                <option value="magnisia">Μαγνησία</option>
                <option value="trikala">Τρίκαλα</option>
                <option value="karditsa">Καρδίτσα</option>
              </optgroup>
              <optgroup label="Στερεά Ελλάδα">
                <option value="fthiotida">Φθιώτιδα</option>
                <option value="viotia">Βοιωτία</option>
                <option value="evia">Εύβοια</option>
                <option value="fokida">Φωκίδα</option>
                <option value="evritania">Ευρυτανία</option>
              </optgroup>
              <optgroup label="Δυτική Ελλάδα">
                <option value="achaia">Αχαΐα</option>
                <option value="aitoloakarnania">Αιτωλοακαρνανία</option>
                <option value="ileia">Ηλεία</option>
              </optgroup>
              <optgroup label="Πελοπόννησος">
                <option value="arkadia">Αρκαδία</option>
                <option value="argolida">Αργολίδα</option>
                <option value="korinthia">Κορινθία</option>
                <option value="lakonia">Λακωνία</option>
                <option value="messinia">Μεσσηνία</option>
              </optgroup>
              <optgroup label="Ιόνια Νησιά">
                <option value="kerkyra">Κέρκυρα</option>
                <option value="lefkada">Λευκάδα</option>
                <option value="kefalonia">Κεφαλονιά</option>
                <option value="zakynthos">Ζάκυνθος</option>
              </optgroup>
              <optgroup label="Βόρειο Αιγαίο">
                <option value="lesvos">Λέσβος</option>
                <option value="chios">Χίος</option>
                <option value="samos">Σάμος</option>
                <option value="ikaria">Ικαρία</option>
                <option value="limnos">Λήμνος</option>
              </optgroup>
              <optgroup label="Νότιο Αιγαίο">
                <option value="kyklades">Κυκλάδες</option>
                <option value="dodekanisa">Δωδεκάνησα</option>
                <option value="rodos">Ρόδος</option>
              </optgroup>
              <optgroup label="Κρήτη">
                <option value="irakleio">Ηράκλειο</option>
                <option value="chania">Χανιά</option>
                <option value="rethymno">Ρέθυμνο</option>
                <option value="lasithi">Λασίθι</option>
              </optgroup>
              <optgroup label="Εξωτερικό">
                <option value="kypros">Κύπρος</option>
                <option value="exoteriko">Άλλο Εξωτερικό</option>
              </optgroup>
              <optgroup label="Διαδικτυακός">
                <option value="web_only">Web Only (Χωρίς Γεωγραφία)</option>
              </optgroup>
            </select>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 items-center border-t border-gray-200 dark:border-gray-700 pt-3">
          <Button onClick={onTestUrl} disabled={editTesting} size="sm" variant="outline">
            {editTesting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
            Δοκιμή URL
          </Button>
          <Button 
            onClick={onFingerprint} 
            disabled={editTesting} 
            size="sm" 
            variant="outline" 
            className="bg-purple-500/20 border-purple-500/50"
          >
            {editTesting ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : '🎤'}
            Fingerprint
          </Button>
          <Button 
            onClick={() => onTestMetadata(station._id)} 
            disabled={metadataDiagnostic?.loading}
            size="sm" 
            variant="outline" 
            className="bg-cyan-500/20 border-cyan-500/50 text-cyan-700 dark:text-cyan-400"
            data-testid="test-metadata-btn"
          >
            {metadataDiagnostic?.loading && metadataDiagnostic?.stationId === station._id 
              ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> 
              : <Search className="w-3 h-3 mr-1" />}
            Διαγνωστικός Metadata
          </Button>
          
          {editTestResult && (
            <span className={`text-sm ${editTestResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {editTestResult.success ? (
                <>✅ {editTestResult.metadata?.title}</>
              ) : (
                <>❌ {editTestResult.error}</>
              )}
            </span>
          )}
          
          <div className="flex-1" />
          
          <Button onClick={onCancel} size="sm" variant="ghost">
            <X className="w-4 h-4 mr-1" /> Ακύρωση
          </Button>
          <Button onClick={onSave} size="sm">
            <Save className="w-4 h-4 mr-1" /> Αποθήκευση
          </Button>
        </div>
        
        {/* Metadata Diagnostic Results */}
        {metadataDiagnostic?.result && metadataDiagnostic?.stationId === station._id && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-2" data-testid="metadata-diagnostic-results">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-400 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Αποτελέσματα Διαγνωστικού
              </h4>
              <Button size="sm" variant="ghost" onClick={onClearDiagnostic} className="text-xs h-6 px-2">
                <X className="w-3 h-3" />
              </Button>
            </div>
            
            {(() => {
              const diag = metadataDiagnostic.result;
              const tests = diag.tests || {};
              const hasAnyData = Object.values(tests).some(s => s?.status === 'OK' || s?.status === 'ok');
              
              return (
                <div className="space-y-2">
                  {/* Summary */}
                  <div className={`p-2 rounded text-xs ${hasAnyData ? 'bg-green-500/10 border border-green-500/30 text-green-700 dark:text-green-400' : 'bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-400'}`}>
                    {hasAnyData ? '✅ Βρέθηκαν δεδομένα metadata' : '❌ Δεν βρέθηκαν δεδομένα metadata από καμία πηγή'}
                  </div>
                  
                  {/* Station URLs info */}
                  <div className="p-2 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700 text-xs space-y-1">
                    {diag.metadata_api_url && <div><span className="text-gray-500">Metadata API:</span> <span className="text-gray-400">{diag.metadata_api_url}</span></div>}
                    {diag.website_url && <div><span className="text-gray-500">Website:</span> <span className="text-gray-400">{diag.website_url}</span></div>}
                    {!diag.metadata_api_url && !diag.website_url && <div className="text-orange-500 font-medium">⚠️ Δεν έχει οριστεί ούτε Metadata API ούτε Website URL!</div>}
                  </div>
                  
                  {/* Test Results */}
                  {Object.entries(tests).map(([key, val]) => {
                    if (!val) return null;
                    const isOk = val.status === 'OK';
                    const label = key === 'metadata_api' ? 'Metadata API' : key === 'website' ? 'Website Scraper' : key === 'onlineradiobox_json' ? 'OnlineRadioBox JSON' : key === 'mytuner_api' ? 'myTuner Radio' : key;
                    return (
                      <div key={key} className={`p-2 rounded border text-xs ${isOk ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`font-bold ${isOk ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
                            {isOk ? '✅' : '❌'} {label}
                          </span>
                          <span className="text-gray-400">({val.status})</span>
                        </div>
                        {isOk && val.result && (
                          <div className="ml-5 text-green-700 dark:text-green-300">
                            <strong>{val.result.artist || '?'}</strong> - {val.result.song_title || val.result.title || '?'}
                            {val.result.source && <span className="text-gray-400 ml-2">({val.result.source})</span>}
                          </div>
                        )}
                        {!isOk && val.error && (
                          <div className="ml-5 text-gray-500">{val.error}</div>
                        )}
                        {!isOk && val.status === 'NO_DATA' && (
                          <div className="ml-5 text-gray-500">Δεν επιστράφηκαν δεδομένα</div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* No tests ran */}
                  {Object.keys(tests).length === 0 && (
                    <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800 text-xs text-yellow-700 dark:text-yellow-400">
                      ⚠️ Δεν εκτελέστηκαν δοκιμές - Βεβαιωθείτε ότι έχετε ρυθμίσει Metadata API URL ή Website URL
                    </div>
                  )}
                  
                  {/* Scraper cache info */}
                  {diag.scraper_cache && Object.keys(diag.scraper_cache).length > 0 && (
                    <div className="p-2 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-200 dark:border-gray-700 text-xs">
                      <span className="text-gray-500 font-medium">Scraper cache:</span>
                      {Object.entries(diag.scraper_cache).map(([domain, scraper]) => (
                        <span key={domain} className="ml-2 text-gray-400">{domain} → {scraper}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EditStationForm;
